import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { Badge } from '../../school_panel/components/ui';

type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent' | 'navy';

const titleCase = (r: string) => r.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Role pills per the platform-admin artboards: Superuser navy, School admin accent, Teacher mist, Student ground. */
export function roleTone(role: string | null | undefined, isSuperuser?: boolean): { label: string; tone: Tone } | null {
  if (isSuperuser) return { label: 'Superuser', tone: 'navy' };
  const r = (role ?? '').trim();
  if (!r) return null;
  if (r === 'school_admin') return { label: 'School admin', tone: 'accent' };
  if (r === 'admin') return { label: 'Admin', tone: 'accent' };
  if (r === 'student') return { label: 'Student', tone: 'neutral' };
  return { label: titleCase(r), tone: 'info' };
}

export function RoleBadge({ role, isSuperuser }: { role: string | null | undefined; isSuperuser?: boolean }) {
  const t = roleTone(role, isSuperuser);
  return t ? <Badge tone={t.tone}>{t.label}</Badge> : <>—</>;
}

/** "G11" from "Grade 11"; any other grade name is shown as is. */
export function gradeShort(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '—';
  const m = n.match(/^grade\s*(\d+)$/i);
  return m ? `G${m[1]}` : n;
}

/** Credit counts: < 1,000 plain integer, < 1M "N.NK", else "N.NNM". */
export function creditsValue(n: number): { value: string; unit?: string } {
  if (n >= 1e6) return { value: (n / 1e6).toFixed(2), unit: 'M' };
  if (n >= 1e3) return { value: (n / 1e3).toFixed(1), unit: 'K' };
  return { value: String(Math.round(n)) };
}
export const compactCredits = (n: number): string => { const c = creditsValue(n); return `${+c.value}${c.unit ?? ''}`; };

/** Mono relative day for signups: "Today", "Yesterday", then "10 Oct" (with the year when it is not this one). */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(now) - start(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

/** Router link that degrades to a plain anchor outside a router (isolated component tests). */
export function AppLink({ to, className, children, ...rest }: { to: string; className?: string; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link to={to} className={className} {...rest}>{children}</Link>
    : <a href={to} className={className} {...rest}>{children}</a>;
}

/** Design `.btn.btn-o` as a link (top-bar "Add ..." actions). */
export const LINK_ACCENT = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] border border-accent bg-accent px-4 py-2 text-sm font-semibold text-navy no-underline transition hover:brightness-105 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
/** Design `.btn.btn-s` as a link. */
export const LINK_SMALL = 'inline-flex min-h-[36px] items-center justify-center gap-2 rounded-[10px] border border-line-strong bg-surface px-3 py-1 text-[13px] font-semibold text-ink no-underline transition hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
